import { Button, Input, Form, message, Spin, Popconfirm } from 'antd'
import {
	DownOutlined,
	CameraOutlined,
	DeleteOutlined,
	UserOutlined,
	LoadingOutlined
} from '@ant-design/icons'
import { FC, useCallback, useEffect, useRef, useState } from 'react'
import { ChangePassword } from '../components/ChangePassword'
import { PageBreadcrumb } from '../components/PageBreadcrumb'
import { authStore } from '../store/auth'
import { useNavigate } from 'react-router-dom'
import { RNLogout } from '../helpers/routeNames'
import { ActivityItem, Session } from '../helpers/types/user'
import {
	getActivity,
	getSessions,
	terminateSession
} from '../api/user'
import '../styles/Profile.scss'

// --- Collapsible section wrapper ---
const Section: FC<{
	title: string
	defaultOpen?: boolean
	children: React.ReactNode
}> = ({ title, defaultOpen = false, children }) => {
	const [open, setOpen] = useState(defaultOpen)
	return (
		<div className="profile-section">
			<div
				className="profile-section-header"
				onClick={() => setOpen((v) => !v)}>
				<h3>{title}</h3>
				<DownOutlined
					className={`section-toggle ${open ? 'open' : ''}`}
				/>
			</div>
			{open && (
				<div className="profile-section-body">{children}</div>
			)}
		</div>
	)
}

const Profile: FC = () => {
	const {
		user,
		profileLoading,
		updateProfile,
		uploadAvatar,
		deleteAvatar
	} = authStore()
	const navigate = useNavigate()

	const [editing, setEditing] = useState(false)
	const [saving, setSaving] = useState(false)
	const [form] = Form.useForm()

	const [sessions, setSessions] = useState<Session[]>([])
	const [sessionsLoading, setSessionsLoading] = useState(false)

	const [activity, setActivity] = useState<ActivityItem[]>([])
	const [activityTotal, setActivityTotal] = useState(0)
	const [activityPage, setActivityPage] = useState(1)
	const [activityLoading, setActivityLoading] = useState(false)

	const avatarInputRef = useRef<HTMLInputElement>(null)

	useEffect(() => {
		if (user && editing) {
			form.setFieldsValue({
				firstName: user.firstName,
				lastName: user.lastName,
				middleName: user.middleName,
				phone: user.phone || '',
				department: user.department || '',
				bio: user.bio || ''
			})
		}
	}, [user, editing, form])

	const handleSave = async () => {
		try {
			const values = await form.validateFields()
			setSaving(true)
			await updateProfile(values)
			setEditing(false)
			message.success('Профиль обновлён')
		} catch {
			// validation or network error
		} finally {
			setSaving(false)
		}
	}

	// --- Sessions ---
	const fetchSessions = useCallback(async () => {
		setSessionsLoading(true)
		try {
			const data = await getSessions()
			setSessions(data)
		} catch {
			// ignore
		} finally {
			setSessionsLoading(false)
		}
	}, [])

	// --- Activity ---
	const fetchActivity = useCallback(async (page: number) => {
		setActivityLoading(true)
		try {
			const res = await getActivity(page)
			setActivity(res.data)
			setActivityTotal(res.total)
			setActivityPage(page)
		} catch {
			// ignore
		} finally {
			setActivityLoading(false)
		}
	}, [])

	// --- Avatar ---
	const handleAvatarUpload = async (file: File) => {
		try {
			await uploadAvatar(file)
			message.success('Аватар обновлён')
		} catch {
			message.error('Не удалось загрузить аватар')
		}
	}

	const handleAvatarDelete = async () => {
		try {
			await deleteAvatar()
			message.success('Аватар удалён')
		} catch {
			message.error('Не удалось удалить аватар')
		}
	}

	const handleTerminate = async (sessionId: string) => {
		try {
			await terminateSession(sessionId)
			setSessions((prev) =>
				prev.filter((s) => s.id !== sessionId)
			)
			message.success('Сессия завершена')
		} catch {
			message.error('Не удалось завершить сессию')
		}
	}

	const fullName = user
		? [user.lastName, user.firstName, user.middleName]
				.filter(Boolean)
				.join(' ')
		: ''

	const initials = user
		? (user.firstName?.[0] || '') + (user.lastName?.[0] || '')
		: ''

	const ROLE_LABELS: Record<string, string> = {
		admin: 'Администратор',
		worker: 'Работник',
		supervisor: 'Супервайзер'
	}
	const roleLabel = user?.role
		? (ROLE_LABELS[user.role] ?? user.role)
		: ''

	if (profileLoading && !user) {
		return (
			<div style={{ textAlign: 'center', padding: 60 }}>
				<Spin
					indicator={<LoadingOutlined spin />}
					size="large"
				/>
			</div>
		)
	}

	if (!user) return <></>

	const avatarSrc = user.avatarUrl
		? user.avatarUrl.startsWith('http')
			? user.avatarUrl
			: `${process.env.REACT_APP_CDN_URL || ''}${user.avatarUrl}`
		: null

	return (
		<div>
			<PageBreadcrumb items={[{ label: 'Профиль' }]} />
			<div className="profile-page">
				{/* --- Identity Header --- */}
				<div className="profile-section">
					<div className="profile-identity">
						<div className="avatar-wrapper">
								{avatarSrc ? (
									<img
										className="avatar-img"
										src={avatarSrc}
									alt={fullName}
								/>
							) : (
								<div className="avatar-img">
									{initials || <UserOutlined />}
								</div>
							)}
							<div className="avatar-actions">
								<button
									type="button"
									style={{
										background: 'var(--default)',
										color: '#fff'
									}}
									onClick={() =>
										avatarInputRef.current?.click()
									}>
									<CameraOutlined />
								</button>
								{user.avatarUrl && (
									<button
										type="button"
										style={{
											background: 'var(--red)',
											color: '#fff'
										}}
										onClick={handleAvatarDelete}>
										<DeleteOutlined />
									</button>
								)}
							</div>
							<input
								ref={avatarInputRef}
								type="file"
								accept="image/*"
								style={{ display: 'none' }}
								onChange={(e) => {
									const file = e.target.files?.[0]
									if (file) handleAvatarUpload(file)
									e.target.value = ''
								}}
							/>
						</div>
						<div className="identity-info">
							<p className="identity-name">
								{fullName}
							</p>
							<p className="identity-role">
								{roleLabel}
								{user.position
									? ` · ${user.position}`
									: ''}
							</p>
						</div>
					</div>
				</div>

				{/* --- Personal Details --- */}
				<Section title="Личные данные" defaultOpen>
					{editing ? (
						<Form
							form={form}
							layout="vertical"
							className="profile-fields">
							<div className="field-group">
								<Form.Item
									name="lastName"
									label="Фамилия"
									rules={[{ required: true }]}>
									<Input size="large" />
								</Form.Item>
							</div>
							<div className="field-group">
								<Form.Item
									name="firstName"
									label="Имя"
									rules={[{ required: true }]}>
									<Input size="large" />
								</Form.Item>
							</div>
							<div className="field-group">
								<Form.Item
									name="middleName"
									label="Отчество">
									<Input size="large" />
								</Form.Item>
							</div>
							<div className="field-group">
								<Form.Item
									name="phone"
									label="Телефон">
									<Input size="large" />
								</Form.Item>
							</div>
							<div className="field-group">
								<Form.Item
									name="department"
									label="Подразделение">
									<Input size="large" />
								</Form.Item>
							</div>
							<div className="field-group">
								<Form.Item name="bio" label="О себе">
									<Input.TextArea rows={3} />
								</Form.Item>
							</div>
							<div className="field-actions">
								<Button
									type="primary"
									onClick={handleSave}
									loading={saving}>
									Сохранить
								</Button>
								<Button
									onClick={() => setEditing(false)}>
									Отмена
								</Button>
							</div>
						</Form>
					) : (
						<div className="profile-fields">
							<div className="field-group">
								<label>Ф.И.О</label>
								<span>{fullName}</span>
							</div>
							{user.phone && (
								<div className="field-group">
									<label>Телефон</label>
									<span>{user.phone}</span>
								</div>
							)}
							{user.department && (
								<div className="field-group">
									<label>Подразделение</label>
									<span>{user.department}</span>
								</div>
							)}
							{user.bio && (
								<div className="field-group">
									<label>О себе</label>
									<span>{user.bio}</span>
								</div>
							)}
							<div className="field-actions">
								<Button
									type="primary"
									onClick={() => setEditing(true)}>
									Редактировать
								</Button>
							</div>
						</div>
					)}
				</Section>

				{/* --- Security --- */}
				<Section title="Безопасность">
					<div className="profile-fields">
						<div className="field-group">
							<label>Логин</label>
							<span>{user.username}</span>
						</div>
						<div className="field-actions">
							<ChangePassword />
						</div>
					</div>
				</Section>

				{/* --- Sessions --- */}
				<Section title="Активные сессии">
					{sessionsLoading ? (
						<Spin indicator={<LoadingOutlined spin />} />
					) : sessions.length === 0 ? (
						<Button
							type="link"
							onClick={fetchSessions}
							style={{ padding: 0 }}>
							Загрузить сессии
						</Button>
					) : (
						<div className="session-list">
							{sessions.map((s) => (
								<div
									className="session-item"
									key={s.id}>
									<div className="session-info">
										<div className="session-agent">
											{s.browser
												? `${s.browser} · ${s.os}`
												: s.userAgent ||
													'Неизвестное устройство'}
										</div>
										<div className="session-meta">
											{s.ipAddress} ·{' '}
											{s.deviceType} ·{' '}
											{new Date(
												s.lastActivityAt ||
													s.createdAt
											).toLocaleDateString(
												'ru'
											)}
										</div>
									</div>
									{s.isCurrent ? (
										<span className="session-current">
											Текущая
										</span>
									) : (
										<Popconfirm
											title="Завершить сессию?"
											onConfirm={() =>
												handleTerminate(s.id)
											}
											okText="Да"
											cancelText="Нет">
											<Button
												size="small"
												danger>
												Завершить
											</Button>
										</Popconfirm>
									)}
								</div>
							))}
						</div>
					)}
				</Section>

				{/* --- Activity --- */}
				<Section title="Активность">
					{activityLoading ? (
						<Spin indicator={<LoadingOutlined spin />} />
					) : activity.length === 0 ? (
						<Button
							type="link"
							onClick={() => fetchActivity(1)}
							style={{ padding: 0 }}>
							Загрузить историю
						</Button>
					) : (
						<div className="activity-list">
							{activity.map((a) => (
								<div
									className="activity-item"
									key={a.id}>
									<span className="activity-text">
										{a.action}
										{a.details
											? `: ${a.details}`
											: ''}
									</span>
									<span className="activity-date">
										{new Date(
											a.createdAt
										).toLocaleDateString('ru')}
									</span>
								</div>
							))}
							{activity.length < activityTotal && (
								<div className="activity-more">
									<Button
										type="link"
										onClick={() =>
											fetchActivity(
												activityPage + 1
											)
										}>
										Загрузить ещё
									</Button>
								</div>
							)}
						</div>
					)}
				</Section>

				{/* --- Account Actions --- */}
				<div className="profile-section">
					<div className="profile-actions">
						<Button
							type="primary"
							danger
							size="large"
							block
							className="logout-btn"
							onClick={() => navigate(RNLogout)}>
							Выход
						</Button>
					</div>
				</div>
			</div>
		</div>
	)
}

export default Profile
